import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ClientService, Client as ClientModel, ClientType, Todo, TenantClientLink } from '../../shared/services/client.service';
import { ProjectService } from '../../shared/services/project.service';
import { TenantService } from '../../shared/services/tenant.service';
import { UserService } from '../../shared/services/user.service';
import { NotificationService } from '../../shared/services/notification.service';
import { ConfirmModalComponent } from '../../shared/utils/modal-validation/modal-validation';
import { Subscription } from 'rxjs';

interface Address {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

@Component({
  selector: 'app-crm',
  imports: [CommonModule, FormsModule, RouterLink, ConfirmModalComponent],
  templateUrl: './crm.html',
  styleUrl: './crm.scss',
})
export class Crm implements OnInit, OnDestroy {
  private clientService = inject(ClientService);
  private projectService = inject(ProjectService);
  private tenantService = inject(TenantService);
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);

  clients: ClientModel[] = [];
  filteredClients: ClientModel[] = [];
  selectedClient: ClientModel | null = null;
  searchQuery = '';
  selectedTenantFilter: string = '';
  /** '' = every kind. A row with no stored type counts as CLIENT. */
  selectedTypeFilter: '' | ClientType = '';

  /** Offered in the form and the filter, in the order they appear there. */
  readonly clientTypes: { value: ClientType; label: string }[] = [
    { value: 'CLIENT', label: 'Client' },
    { value: 'SUPPLIER', label: 'Supplier' },
    { value: 'ARCHITECT', label: 'Architect' },
    { value: 'ENGINEER', label: 'Engineer' },
    { value: 'OTHER', label: 'Other' },
  ];

  typeLabel(type: ClientType | null | undefined): string {
    return this.clientTypes.find(t => t.value === (type ?? 'CLIENT'))?.label ?? 'Client';
  }

  /** Pill colours, one per kind, so the list scans by shape as well as by word. */
  typeClass(type: ClientType | null | undefined): string {
    switch (type ?? 'CLIENT') {
      case 'SUPPLIER': return 'bg-amber-50 text-amber-700 border-amber-100';
      case 'ARCHITECT': return 'bg-violet-50 text-violet-700 border-violet-100';
      case 'ENGINEER': return 'bg-cyan-50 text-cyan-700 border-cyan-100';
      case 'OTHER': return 'bg-gray-100 text-gray-500 border-gray-200';
      default: return 'bg-blue-50 text-blue-700 border-blue-100';
    }
  }
  isLoading = true;

  // Tab management
  activeTab: 'info' | 'todos' | 'projects' = 'info';

  // Client form
  showClientModal = false;
  isEditingClient = false;
  clientForm: Partial<ClientModel> = {};
  addressForm: Address = {};

  // Todo management
  clientTodos: any[] = [];
  clientProjects: any[] = [];
  showTodoModal = false;
  showDeleteClientConfirm = false;
  todoForm: Partial<Todo> = {};
  isEditingTodo = false;
  hasDueDate = false;
  // Todo scope — cascading tenant → client → project
  tenants: any[] = [];
  filteredClientsForTodo: ClientModel[] = [];
  filteredProjectsForTodo: any[] = [];

  // User list for todo assignment
  users: any[] = [];
  selectedUsers: string[] = [];
  currentUser: any = null;

  // Read-only task detail ("click-to-view")
  showTaskDetail = false;
  detailTask: any = null;
  detailAssignedUsers: any[] = [];

  // Multi-tenant selection for client create/edit
  selectedTenantIds: Set<string> = new Set();
  clientTenantLinks: TenantClientLink[] = [];

  countries = [
    { name: 'Belgique', value: 'BELGIQUE' },
    { name: 'France', value: 'FRANCE' },
    { name: 'Suisse', value: 'SUISSE' },
    { name: 'Luxembourg', value: 'LUXEMBOURG' },
    { name: 'Pays-Bas', value: 'PAYS-BAS' },
    { name: 'Allemagne', value: 'ALLEMAGNE' },
  ]

  private subscriptions: Subscription[] = [];

  async ngOnInit() {
    this.isLoading = true;
    // Fetch all init data in parallel for speed
    await Promise.all([
      this.clientService.init(),
      this.userService.init(),
      this.tenantService.init(),
    ]);

    this.subscriptions.push(
      this.tenantService.tenants$.subscribe(tenants => {
        this.tenants = tenants;
      })
    );
    this.subscriptions.push(
      this.clientService.clients$.subscribe(clients => {
        this.clients = clients;
        this.filterClients();
        this.isLoading = false;
        this.cdr.markForCheck();

        // Check for query params to auto-select client and todo
        this.handleQueryParams();
      })
    );

    this.subscriptions.push(
      this.userService.users$.subscribe(users => {
        this.users = users;
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.push(
      this.userService.currentUser$.subscribe(user => {
        this.currentUser = user;
      })
    );
  }

  private async handleQueryParams() {
    const todoId = this.route.snapshot.queryParamMap.get('todoId');
    const clientName = this.route.snapshot.queryParamMap.get('clientName');

    if (todoId && clientName && this.clients.length > 0) {
      console.log('[CRM] Opening from dashboard - todoId:', todoId, 'clientName:', clientName);

      // Find the client by name
      const client = this.clients.find(c => c.name === clientName);

      if (client) {
        // Select the client
        await this.selectClient(client);

        // Switch to todos tab
        this.activeTab = 'todos';

        // Wait a bit for todos to load
        setTimeout(async () => {
          // Find the todo in the loaded todos
          const todoLink = this.clientTodos.find(tl => tl.todo?.id === todoId);

          if (todoLink) {
            console.log('[CRM] Found todo, opening edit modal');
            // Open the todo in edit mode
            await this.openEditTodoModal(new Event('click'), todoLink);
          } else {
            console.warn('[CRM] Todo not found in client todos');
          }
        }, 500);
      } else {
        console.warn('[CRM] Client not found:', clientName);
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  filterClients() {
    const query = this.searchQuery.toLowerCase().trim();
    this.filteredClients = this.clients.filter(client =>
      (!this.selectedTypeFilter || (client.type ?? 'CLIENT') === this.selectedTypeFilter) &&
      (!query ||
        client.name.toLowerCase().includes(query) ||
        (client.email && client.email.toLowerCase().includes(query)) ||
        (client.phone && client.phone.toLowerCase().includes(query)))
    );
  }

  async onTenantFilterChange() {
    this.isLoading = true;
    this.selectedClient = null;
    await this.clientService.filterByTenant(this.selectedTenantFilter || null);
    this.isLoading = false;
  }

  getTenantName(tenantId: string | null | undefined): string | null {
    if (!tenantId) return null;
    return this.tenants.find(t => t.id === tenantId)?.name ?? null;
  }

  /** Soft monogram palette (background + foreground), picked deterministically per client name. */
  private static readonly MONOGRAM_PALETTE = [
    { bg: '#EEF2FF', fg: '#4338CA' }, // indigo
    { bg: '#ECFEFF', fg: '#0E7490' }, // cyan
    { bg: '#ECFDF5', fg: '#047857' }, // emerald
    { bg: '#FEF3C7', fg: '#B45309' }, // amber
    { bg: '#FCE7F3', fg: '#BE185D' }, // pink
    { bg: '#F3E8FF', fg: '#7E22CE' }, // purple
    { bg: '#FFE4E6', fg: '#BE123C' }, // rose
    { bg: '#E0F2FE', fg: '#0369A1' }, // sky
  ];

  /** A colored monogram (initials + deterministic color) for a client, so the list is easy to scan. */
  monogram(name: string | null | undefined): { bg: string; fg: string; text: string } {
    const clean = (name ?? '').trim();
    const words = clean.split(/\s+/).filter(Boolean);
    const text = (
      words.length >= 2 ? words[0].charAt(0) + words[1].charAt(0) : clean.slice(0, 2)
    ).toUpperCase() || '?';
    let hash = 0;
    for (let i = 0; i < clean.length; i++) hash = (hash * 31 + clean.charCodeAt(i)) >>> 0;
    const palette = Crm.MONOGRAM_PALETTE;
    return { ...palette[hash % palette.length], text };
  }

  async selectClient(client: ClientModel) {
    this.selectedClient = client;
    this.activeTab = 'info' as 'info' | 'todos' | 'projects';

    // Sync addressForm with selected client for the edit modal
    if (client.address) {
      this.addressForm = { ...client.address };
    } else {
      this.addressForm = { street: '', city: '', state: '', zip: '', country: '' };
    }

    // Load todos and projects in parallel
    await Promise.all([this.loadClientTodos(), this.loadClientProjects()]);

    // Try to load tenant links from the link table; fallback to legacy tenantId
    const tenantLinks = await this.clientService.getTenantsForClient(client.id).catch(() => [] as TenantClientLink[]);
    if (tenantLinks.length > 0) {
      this.clientTenantLinks = tenantLinks;
      this.selectedTenantIds = new Set(tenantLinks.map(l => l.tenantId));
    } else if (client.tenantId) {
      const tenant = this.tenants.find(t => t.id === client.tenantId);
      this.clientTenantLinks = [{
        id: 'legacy-0', tenantId: client.tenantId, clientId: client.id,
        tenant: tenant ? { id: tenant.id, name: tenant.name } : null,
      }];
      this.selectedTenantIds = new Set([client.tenantId]);
    } else {
      this.clientTenantLinks = [];
      this.selectedTenantIds = new Set();
    }
    this.cdr.detectChanges();
  }

  async loadClientProjects() {
    if (!this.selectedClient) return;
    this.clientProjects = await this.clientService.getProjectsByClient(this.selectedClient.id);
    this.cdr.markForCheck();
  }

  async loadClientTodos() {
    if (!this.selectedClient) return;

    // Fetch all todos directly linked via clientId field
    const { todos } = await this.clientService.fetchTodosPage({
      clientId: this.selectedClient.id,
      limit: 100,
    });
    this.clientTodos = todos;
    this.cdr.markForCheck();
  }

  // CLIENT CRUD OPERATIONS
  openNewClientModal() {
    this.isEditingClient = false;
    this.clientForm = { name: '', type: 'CLIENT', email: '', phone: '', tenantId: undefined };
    this.selectedTenantIds = new Set();
    this.addressForm = { street: '', city: '', state: '', zip: '', country: '' };
    this.showClientModal = true;
  }

  openEditClientModal() {
    if (!this.selectedClient) return;
    this.isEditingClient = true;
    this.clientForm = { ...this.selectedClient };
    // selectedTenantIds already loaded from selectClient()
    if (this.selectedClient.address) {
      this.addressForm = { ...this.selectedClient.address };
    } else {
      this.addressForm = { street: '', city: '', state: '', zip: '', country: '' };
    }
    this.showClientModal = true;
  }

  toggleTenantSelection(tenantId: string) {
    if (this.selectedTenantIds.has(tenantId)) {
      this.selectedTenantIds.delete(tenantId);
    } else {
      this.selectedTenantIds.add(tenantId);
    }
  }

  isTenantSelected(tenantId: string): boolean {
    return this.selectedTenantIds.has(tenantId);
  }

  buildFullAddress(): string {
    const parts: string[] = [];

    if (this.addressForm.street?.trim()) {
      parts.push(this.addressForm.street.trim());
    }
    if (this.addressForm.city?.trim()) {
      parts.push(this.addressForm.city.trim());
    }
    if (this.addressForm.state?.trim()) {
      parts.push(this.addressForm.state.trim());
    }
    if (this.addressForm.zip?.trim()) {
      parts.push(this.addressForm.zip.trim());
    }
    if (this.addressForm.country?.trim()) {
      parts.push(this.addressForm.country.trim());
    }

    return parts.join(', ');
  }

  async saveClient() {
    if (!this.clientForm.name?.trim()) {
      alert('Client name is required');
      return;
    }
    try {
      const fullAddress = this.buildFullAddress();
      const hasAddressData = Object.values(this.addressForm).some(val => val && val.trim());
      const clientData: Partial<ClientModel> = {
        name: this.clientForm.name.trim(),
        type: this.clientForm.type ?? 'CLIENT',
        email: this.clientForm.email?.trim() || undefined,
        phone: this.clientForm.phone?.trim() || undefined,
        address: hasAddressData ? this.addressForm : undefined,
        fullAddress: fullAddress || undefined,
        tenantId: [...this.selectedTenantIds][0] || undefined,
        tenantIds: [...this.selectedTenantIds],
      };

      if (this.isEditingClient && this.selectedClient) {
        const updatedClient = await this.clientService.updateClient(this.selectedClient.id, clientData);
        if (updatedClient) {
          await this.clientService.syncClientTenants(this.selectedClient.id, [...this.selectedTenantIds]).catch(() => { });
          this.selectedClient = {
            ...updatedClient,
            address: updatedClient.address ? (typeof updatedClient.address === 'string' ? JSON.parse(updatedClient.address) : updatedClient.address) : null
          };
        }
      } else {
        const newClient = await this.clientService.createClient(clientData);
        if (newClient) {
          this.selectedClient = {
            ...newClient,
            address: newClient.address ? (typeof newClient.address === 'string' ? JSON.parse(newClient.address) : newClient.address) : null
          };
        }
      }

      // Build local tenant links for immediate UI display
      this.clientTenantLinks = [...this.selectedTenantIds].map((tId, i) => {
        const tenant = this.tenants.find(t => t.id === tId);
        return {
          id: `local-${i}`, tenantId: tId, clientId: this.selectedClient?.id || '',
          tenant: tenant ? { id: tenant.id, name: tenant.name } : null,
        };
      });

      this.showClientModal = false;
      this.clientForm = {};
      this.addressForm = {};
      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('[CRM] Error saving client:', error);
      alert('Failed to save client: ' + (error.message || error));
    }
  }

  deleteClient(event?: Event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    if (!this.selectedClient) return;
    this.showDeleteClientConfirm = true;
  }

  async handleDeleteClientConfirm(result: 'ok' | 'cancel') {
    this.showDeleteClientConfirm = false;
    if (result !== 'ok') return;
    const deletedId = this.selectedClient!.id;
    try {
      // Unlink any projects still linked to this client first
      const linkedProjects = await this.clientService.getProjectsByClient(deletedId);
      if (linkedProjects.length > 0) {
        await Promise.all(
          linkedProjects.map((p: any) =>
            this.projectService.updateProject(p.id, { clientId: undefined })
          )
        );
      }
      await this.clientService.deleteClient(deletedId);
      // Optimistic removal from local lists
      this.clients = this.clients.filter(c => c.id !== deletedId);
      this.filteredClients = this.filteredClients.filter(c => c.id !== deletedId);
      this.selectedClient = null;
      this.clientTodos = [];
      this.clientProjects = [];
      this.cdr.markForCheck();
    } catch (error: any) {
      console.error('[CRM] Error deleting client:', error);
      alert('Failed to delete client: ' + (error.message || error));
    }
  }

  closeClientModal() {
    this.showClientModal = false;
    this.clientForm = {};
    this.addressForm = {};
  }


  // TODO OPERATIONS
  openNewTodoModal() {
    this.isEditingTodo = false;
    this.hasDueDate = false;
    this.todoForm = {
      name: '',
      description: '',
      status: 'TODO',
      priority: 'LOW',
      dueDate: '',
      // Pre-fill tenant + client if a client is selected in the CRM
      tenantId: undefined,
      clientId: undefined,
      projectId: undefined,
    };
    this.filteredClientsForTodo = [];
    this.filteredProjectsForTodo = [];
    this.selectedUsers = this.currentUser ? [this.currentUser.id] : [];
    this.showTodoModal = true;
  }

  onTodoTenantChange() {
    // Filter clients: all clients (no tenant FK on Client yet) — show all for now
    this.filteredClientsForTodo = this.clients;
    this.todoForm.clientId = undefined;
    this.todoForm.projectId = undefined;
    this.filteredProjectsForTodo = [];
  }

  async onTodoClientChange() {
    this.todoForm.projectId = undefined;
    this.filteredProjectsForTodo = [];
    if (this.todoForm.clientId) {
      const projects = await this.clientService.getProjectsByClient(this.todoForm.clientId);
      this.filteredProjectsForTodo = projects;
    }
  }


  async openEditTodoModal(event: Event, todo: Todo) {
    event.preventDefault();
    event.stopPropagation();

    const rawDate = todo.dueDate;
    this.hasDueDate = !!rawDate && rawDate !== '';
    let formattedDate = '';
    if (this.hasDueDate) {
      formattedDate = rawDate!.split('T')[0].split(' ')[0];
    }

    this.isEditingTodo = true;
    this.todoForm = {
      id: todo.id,
      name: todo.name,
      description: todo.description || '',
      status: todo.status || 'TODO',
      priority: todo.priority || 'LOW',
      dueDate: formattedDate,
      tenantId: todo.tenantId ?? undefined,
      clientId: todo.clientId ?? undefined,
      projectId: todo.projectId ?? undefined,
    };

    try {
      const userLinks = await this.clientService.getUsersByTodo(todo.id);
      this.selectedUsers = userLinks.map((ul: any) => ul.userId);
    } catch {
      this.selectedUsers = [];
    }

    this.showTodoModal = true;
  }

  onDueDateToggle() {
    if (this.hasDueDate) {
      if (!this.todoForm.dueDate) {
        // Default to today in local time format YYYY-MM-DD
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        this.todoForm.dueDate = `${year}-${month}-${day}`;
      }
    } else {
      this.todoForm.dueDate = '';
    }
  }

  private formatDueDateForSave(): string | null {
    if (this.hasDueDate && this.todoForm.dueDate && this.todoForm.dueDate.trim() !== '') {
      try {
        let dateStr = this.todoForm.dueDate.trim();
        // If it comes from the date picker, it's just YYYY-MM-DD
        if (!dateStr.includes('T')) {
          dateStr = `${dateStr}T12:00:00.000Z`;
        }
        // Force parsing through Date to ensure it's a fully valid ISO-8601 string
        const parsedDate = new Date(dateStr);
        if (isNaN(parsedDate.getTime())) {
          return null;
        }
        return parsedDate.toISOString();
      } catch (e) {
        console.warn('[CRM] Failed to format dueDate for save', e);
        return null;
      }
    }
    return null;
  }

  async saveTodo() {
    if (!this.todoForm.name?.trim()) {
      alert('Todo name is required');
      return;
    }

    console.log('[CRM] saveTodo called - isEditing:', this.isEditingTodo);

    try {
      if (this.isEditingTodo && this.todoForm.id) {
        // UPDATE existing todo
        console.log('[CRM] Updating todo:', this.todoForm.id);

        const updatedTodo = await this.clientService.updateTodo(this.todoForm.id, {
          name: this.todoForm.name,
          description: this.todoForm.description,
          status: this.todoForm.status,
          priority: this.todoForm.priority,
          dueDate: this.formatDueDateForSave(),
          tenantId: this.todoForm.tenantId,
          clientId: this.todoForm.clientId,
          projectId: this.todoForm.projectId,
        });

        if (updatedTodo) {
          // Get current user assignments
          const currentUserLinks = await this.clientService.getUsersByTodo(this.todoForm.id);
          const currentUserIds = currentUserLinks.map((ul: any) => ul.userId);

          // Find users to add (in selectedUsers but not in currentUserIds)
          const usersToAdd = this.selectedUsers.filter(uid => !currentUserIds.includes(uid));

          // Find users to remove (in currentUserIds but not in selectedUsers)
          const usersToRemove = currentUserLinks.filter((ul: any) => !this.selectedUsers.includes(ul.userId));

          // Add new user assignments
          for (const userId of usersToAdd) {
            await this.clientService.linkTodoToUser(this.todoForm.id, userId);
          }

          // Remove unselected user assignments
          for (const userLink of usersToRemove) {
            await this.clientService.unlinkTodoFromUser(userLink.id);
          }

          console.log('[CRM] Todo updated successfully');

          // Notify newly assigned users
          if (this.currentUser?.id) {
            const senderName = [this.currentUser.firstName, this.currentUser.name].filter(Boolean).join(' ') || 'A colleague';
            this.notificationService.notifyTodoAssignment({
              todoName: this.todoForm.name || 'Untitled',
              newUserIds: this.selectedUsers,
              previousUserIds: currentUserIds,
              currentUserId: this.currentUser.id,
              senderName,
              todoId: this.todoForm.id,
            }).catch(e => console.error('[CRM] Notification error:', e));
          }
        }

      } else {
        // CREATE new todo
        console.log('[CRM] Creating new todo');

        // Normalize empty string date to null, respecting the toggle
        const todoToCreate = {
          ...this.todoForm,
          dueDate: this.formatDueDateForSave()
        };

        const newTodo = await this.clientService.createTodo(todoToCreate);

        if (newTodo && this.selectedClient) {
          // Link todo to client
          await this.clientService.linkTodoToClient(newTodo.id, this.selectedClient.id);

          // Link todo to selected users
          for (const userId of this.selectedUsers) {
            await this.clientService.linkTodoToUser(newTodo.id, userId);
          }

          console.log('[CRM] Todo created successfully');

          // Notify newly assigned users
          if (this.currentUser?.id) {
            const senderName = [this.currentUser.firstName, this.currentUser.name].filter(Boolean).join(' ') || 'A colleague';
            this.notificationService.notifyTodoAssignment({
              todoName: this.todoForm.name || 'Untitled',
              newUserIds: this.selectedUsers,
              previousUserIds: [],
              currentUserId: this.currentUser.id,
              senderName,
              todoId: newTodo.id,
            }).catch(e => console.error('[CRM] Notification error:', e));
          }
        }
      }

      await this.loadClientTodos();
      this.showTodoModal = false;
      this.todoForm = {};
      this.selectedUsers = [];
    } catch (error: any) {
      console.error('[CRM] Error saving todo:', error);
      alert('Failed to save todo: ' + (error.message || error));
    }
  }

  async deleteTodo(event: Event, todo: Todo) {
    event.preventDefault();
    event.stopPropagation();

    const confirmed = confirm(`Are you sure you want to delete "${todo.name}"?\n\nThis action cannot be undone.`);
    if (!confirmed) return;

    try {
      await this.clientService.deleteTodo(todo.id);
      await this.loadClientTodos();
    } catch (error) {
      console.error('[CRM] Error deleting todo:', error);
      alert('Failed to delete todo');
    }
  }

  closeTodoModal() {
    this.showTodoModal = false;
    this.todoForm = {};
    this.selectedUsers = [];
  }

  // ── Read-only task detail ("click-to-view") ─────────────────────────────────
  openTaskDetail(todo: any) {
    this.detailTask = todo;
    this.detailAssignedUsers = [];
    this.showTaskDetail = true;
    this.clientService.getUsersByTodo(todo.id).then((links: any[]) => {
      const ids = links.map(l => l.userId);
      this.detailAssignedUsers = this.users.filter((u: any) => ids.includes(u.id));
      this.cdr.markForCheck();
    }).catch(() => { });
  }

  closeTaskDetail() {
    this.showTaskDetail = false;
    this.detailTask = null;
    this.detailAssignedUsers = [];
  }

  editFromTaskDetail() {
    const todo = this.detailTask;
    this.closeTaskDetail();
    if (todo) this.openEditTodoModal(new Event('click'), todo);
  }

  async deleteFromTaskDetail() {
    const todo = this.detailTask;
    this.closeTaskDetail();
    if (todo) await this.deleteTodo(new Event('click'), todo);
  }

  getInitials(u: any): string {
    return `${(u?.firstName || u?.name || '?').charAt(0)}${u?.firstName && u?.name ? u.name.charAt(0) : ''}`.toUpperCase();
  }

  /** Accent color for the task-detail hero strip, keyed to status. */
  get detailStatusAccent(): string {
    switch (this.detailTask?.status) {
      case 'IN_PROGRESS': return 'bg-blue-500';
      case 'DONE': return 'bg-emerald-500';
      default: return 'bg-slate-400';
    }
  }

  /** True when the detail task is past due and not done. */
  get detailIsOverdue(): boolean {
    return !!this.detailTask && this.detailTask.status !== 'DONE' && this.isTodoOverdue(this.detailTask.dueDate);
  }

  /** Human-readable distance to the detail task's due date. */
  get detailDueRelative(): string {
    const due = this.detailTask?.dueDate;
    if (!due) return '';
    const d = new Date(due);
    if (isNaN(d.getTime())) return '';
    const sod = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const diff = Math.round((sod(d) - sod(new Date())) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff === -1) return 'Yesterday';
    if (diff < 0) return `${-diff} days overdue`;
    return `In ${diff} days`;
  }

  toggleUserSelection(userId: string) {
    const index = this.selectedUsers.indexOf(userId);
    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers.push(userId);
    }
  }

  /**
   * Users offered in the "Assign Users" picker: active ones, plus any archived
   * user already assigned so an edit doesn't hide an existing assignment.
   * `users` keeps everybody so archived names still resolve elsewhere.
   */
  get assignableUsers(): any[] {
    return this.users.filter(u => !u.isArchived || (u.id ? this.selectedUsers.includes(u.id) : false));
  }

  isUserSelected(userId: string): boolean {
    return this.selectedUsers.includes(userId);
  }

  getTodoStatusBadgeClass(status: string | null | undefined): string {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'TODO':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-500 border-gray-200';
    }
  }

  getTodoStatusLabel(status: string | null | undefined): string {
    switch (status) {
      case 'DONE':
        return 'Done';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'TODO':
        return 'To Do';
      default:
        return 'To Do';
    }
  }

  getTodoPriorityBadgeClass(priority: string | null | undefined): string {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'LOW':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-500 border-gray-200';
    }
  }

  getTodoPriorityLabel(priority: string | null | undefined): string {
    switch (priority) {
      case 'HIGH':
        return 'High';
      case 'MEDIUM':
        return 'Medium';
      case 'LOW':
        return 'Low';
      default:
        return 'Medium';
    }
  }

  isTodoOverdue(dueDate: string | null | undefined): boolean {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  }
}
